import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { searchAPI } from '../services/api';

const DEBOUNCE_MS = 300;

export type GlobalSearchOption = {
  type: 'case';
  id: number;
  label: string;
  sublabel?: string;
  firmId?: number;
};

const GlobalSearch: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<GlobalSearchOption[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(inputValue.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const runSearch = async () => {
      setLoading(true);
      try {
        const res = await searchAPI.global(query);
        if (cancelled) return;

        const data = res.data?.data || {};
        const nextOptions: GlobalSearchOption[] = [];

        (data.cases || []).forEach((caseItem: any) => {
          nextOptions.push({
            type: 'case',
            id: caseItem.id,
            label: caseItem.title,
            sublabel: [
              caseItem.client_reference_id ? `Client ID ${caseItem.client_reference_id}` : '',
              caseItem.file_number ? `File ${caseItem.file_number}` : '',
              caseItem.project?.firm?.name,
              caseItem.status,
            ].filter(Boolean).join(' · '),
            firmId: caseItem.project?.firm_id,
          });
        });

        setOptions(nextOptions);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const handleSelect = (_event: React.SyntheticEvent, value: GlobalSearchOption | null) => {
    if (!value) return;

    if (value.type === 'case' && value.firmId) {
      window.open(`/firms/${value.firmId}/cases/${value.id}`, '_blank', 'noopener,noreferrer');
    }

    setInputValue('');
    setOpen(false);
  };

  const noOptionsText = useMemo(() => {
    if (inputValue.trim().length < 2) return 'Type at least 2 characters';
    if (loading) return 'Searching...';
    return 'No results found';
  }, [inputValue, loading]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_event, value) => setInputValue(value)}
      onChange={handleSelect}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
      noOptionsText={noOptionsText}
      sx={{
        width: { xs: 160, sm: 240, md: 320 },
        mr: { xs: 0.5, sm: 1 },
        '& .MuiInputBase-root': {
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'inherit',
        },
        '& .MuiInputBase-input': {
          color: 'inherit',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.3)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.5)',
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.type}-${option.id}`}>
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.sublabel && (
              <Typography variant="caption" color="text.secondary">
                {option.sublabel}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search cases..."
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default GlobalSearch;
